/**
 * Documents Tab Component for Event Details Dialog
 */
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { File, Upload, Download, Trash2, Eye, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DocumentsTab({ 
  booking, 
  documents = [], 
  onDocumentsUpdate,
  formatDate 
}) {
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/bookings/${booking.id}/documents`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onDocumentsUpdate();
      toast.success('Document uploaded');
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      await axios.delete(`${API}/bookings/${booking.id}/documents/${docId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      onDocumentsUpdate();
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handlePreview = (doc) => {
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.filename);
    const isPdf = /\.pdf$/i.test(doc.filename);
    
    if (isImage || isPdf) {
      setPreviewUrl(doc.url);
      setShowPreview(true);
    } else {
      window.open(doc.url, '_blank');
    }
  };

  const getFileIcon = (filename) => {
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(filename)) return '🖼️';
    if (/\.pdf$/i.test(filename)) return '📄';
    if (/\.(doc|docx)$/i.test(filename)) return '📝';
    if (/\.(xls|xlsx)$/i.test(filename)) return '📊';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <File className="w-5 h-5 text-blue-400" />
          Documents
          {documents.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-sm">
              {documents.length}
            </span>
          )}
        </h3>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
          />
          <Button 
            size="sm" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-neon-blue hover:bg-neon-blue/90"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-1" />
            )}
            Upload
          </Button>
        </div>
      </div>

      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border group"
            >
              <span className="text-2xl">{getFileIcon(doc.filename)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{doc.filename}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {doc.size && <span>{formatFileSize(doc.size)}</span>}
                  {doc.uploaded_at && (
                    <span>Uploaded {formatDate ? formatDate(doc.uploaded_at) : doc.uploaded_at}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handlePreview(doc)}
                  className="h-8 w-8"
                  title="Preview"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => window.open(doc.url, '_blank')}
                  className="h-8 w-8"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleDelete(doc.id)}
                  className="h-8 w-8 text-red-400 hover:text-red-300"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <File className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No documents uploaded</p>
          <p className="text-sm">Upload files to share with your client</p>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-card border border-border max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Document Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[400px]">
            {/\.(jpg|jpeg|png|gif|webp)$/i.test(previewUrl) ? (
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain" />
            ) : (
              <iframe src={previewUrl} className="w-full h-[70vh]" title="Document Preview" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
